import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import CrudManager from "@/pages/admin/CrudManager";
import AmazonBulk from "@/pages/admin/AmazonBulk";
import AdminHelp from "@/pages/admin/AdminHelp";
import AutoBlog from "@/pages/admin/AutoBlog";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminBacklinks from "@/pages/admin/AdminBacklinks";
import AdminPreviews from "@/pages/admin/AdminPreviews";
import AdminAutoPost from "@/pages/admin/AdminAutoPost";
import AdminFloorModels from "@/pages/admin/AdminFloorModels";
import {
  adminAnalytics, adminAnalyticsDetailed, adminContacts, adminSubscribers, adminGetSettings, adminUpdateSettings, aiGenerateBlog,
  adminRevenueEstimate,
} from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, FileText, Megaphone, Link2, Anchor, Share2,
  Mail, Users, LogOut, Eye, TrendingUp, DollarSign, Inbox, ExternalLink, HelpCircle, Zap,
} from "lucide-react";

const CATS_BLOG = ["Web Development", "SEO", "Marketing", "Entrepreneurship", "Artificial Intelligence", "Technology", "Business"];

const BLOG_FIELDS = [
  { key: "title", label: "Title", hint: "Aim for 40-70 chars for SEO." }, { key: "slug", label: "Slug", hint: "URL-friendly ID (e.g. how-to-grow-in-2026). Use the SEO Slug tool." },
  { key: "category", label: "Category", type: "select", options: CATS_BLOG },
  { key: "author", label: "Author" }, { key: "read_time", label: "Read time", hint: "e.g. '5 min read'" },
  { key: "date", label: "Date (YYYY-MM-DD)" }, { key: "image", label: "Image URL", full: true, hint: "1200×630 or similar 16:9 hero image." },
  { key: "excerpt", label: "Excerpt", type: "textarea", rows: 2, hint: "1-2 sentences shown in cards & meta description." },
  { key: "content", label: "Content (use ## for headings)", type: "textarea", rows: 8, hint: "Markdown. Sprinkle affiliate links naturally with [text](url) — see Help tab." },
  { key: "tags", label: "Tags", type: "list", hint: "Comma-separated. Improves search & related posts." }, { key: "featured", label: "Featured", type: "bool" },
];
const PRODUCT_FIELDS = [
  { key: "name", label: "Name", hint: "Short, clear product name." },
  { key: "type", label: "Type", type: "select", options: ["affiliate"], hint: "The shop is affiliate-only — external links to partner stores. Own products arrive later via the Shopify integration." },
  { key: "category", label: "Category", hint: "Try: Website Resources · Marketing Resources · AI Prompt Collections · Templates · Books · Merchandise — slots into /shop automatically." },
  { key: "price", label: "Price (USD)", type: "number", hint: "Leave 0 — prices shown come from the partner site." },
  { key: "image", label: "Image URL", full: true, hint: "Direct link ending in .jpg/.png/.webp. Amazon imports fill this automatically." },
  { key: "description", label: "Description", type: "textarea", rows: 6, hint: "Honest, benefit-first. Why YOU recommend it." },
  { key: "affiliate_url", label: "Affiliate URL", full: true, hint: "Paste your tracking link from Amazon Associates, Impact, PartnerStack, ShareASale, etc." },
  { key: "brand", label: "Brand", hint: "e.g. Amazon, Notion, Sony. Shown in search rich snippets." },
];
const AD_FIELDS = [
  { key: "title", label: "Ad Title", hint: "Not shown publicly — for your reference." },
  { key: "placement", label: "Placement", type: "select", options: ["sidebar", "blog"] },
  { key: "image", label: "Image URL", full: true, hint: "Sidebar: 600×400. In-blog: 1200×628." }, { key: "link", label: "Link URL", full: true, hint: "Your affiliate/tracking link — every click is tracked." },
  { key: "active", label: "Active", type: "bool" },
];

const PROGRAMS_FIELDS = [
  { key: "name", label: "Name", hint: "Program or company name (e.g. AVENO)." },
  { key: "industry", label: "Industry", hint: "e.g. E-commerce, Education, SaaS." },
  { key: "role", label: "Your role", hint: "e.g. Founder, Creator, Technical partner." },
  { key: "logo", label: "Image URL", full: true, hint: "Logo or cover image, 16:9 works best." },
  { key: "services", label: "What you built/support", type: "list", hint: "Comma-separated, e.g. Brand, Website, Store setup." },
];

const NAV = [
  ["overview", "Overview", LayoutDashboard],
  ["leads", "Leads", Inbox],
  ["pricing", "Pricing", DollarSign],
  ["backlinks", "Backlinks", Anchor],
  ["previews", "AI Previews", Eye],
  ["help", "Help & Instructions", HelpCircle],
  ["blog", "Blog", FileText],
  ["auto-blog", "Auto Blog", Zap],
  ["floor-planner", "Floor Planner", Share2],
  ["ads", "Ads", Megaphone],
  ["affiliate", "Affiliate Links", Link2],
  ["programs", "Programs", Share2],
  ["social", "Social & Profile", Share2],
  ["messages", "Messages", Mail],
  ["subscribers", "Subscribers", Users],
];

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="border border-border p-6 bg-card">
      <Icon className="w-6 h-6 text-primary mb-4" />
      <div className="font-display text-4xl">{value}</div>
      <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mt-2">{label}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function Overview() {
  const [a, setA] = useState(null);
  useEffect(() => { adminAnalytics().then(setA).catch(() => {}); }, []);
  if (!a) return <p className="text-muted-foreground">Loading analytics…</p>;
  const max = Math.max(1, ...a.views_by_day.map((d) => d.views));
  return (
    <div data-testid="admin-overview">
      <h2 className="font-display text-3xl mb-8">Overview</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Eye} label="Total Views" value={a.total_views} sub={`${a.views_30d} in last 30 days`} />
        <StatCard icon={Users} label="Subscribers" value={a.subscribers} />
        <StatCard icon={Inbox} label="Messages" value={a.contacts} sub={`${a.unread_contacts} unread`} />
        <StatCard icon={DollarSign} label="Revenue" value={`$${a.revenue}`} sub={`${a.orders} orders`} />
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-border p-6 bg-card">
          <p className="overline text-primary mb-6 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Page views (14 days)</p>
          <div className="flex items-end gap-1.5 h-48">
            {a.views_by_day.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center justify-end group">
                <div className="w-full bg-primary/70 hover:bg-primary transition-colors" style={{ height: `${(d.views / max) * 100}%`, minHeight: d.views ? "4px" : "0" }} title={`${d.date}: ${d.views}`} />
                <span className="font-mono text-[9px] text-muted-foreground mt-2 rotate-45 origin-left whitespace-nowrap">{d.date.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-border p-6 bg-card">
          <p className="overline text-primary mb-6">Top Pages</p>
          <div className="space-y-3">
            {a.top_pages.map((p) => (
              <div key={p.path} className="flex items-center justify-between text-sm border-b border-border pb-2">
                <span className="truncate text-foreground/80">{p.path}</span>
                <span className="font-mono text-primary">{p.count}</span>
              </div>
            ))}
            {a.top_pages.length === 0 && <p className="text-sm text-muted-foreground">No data yet.</p>}
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        <StatCard icon={FileText} label="Blog Posts" value={a.posts} />
      </div>
      <RevenueWidget />
      <DeepAnalytics />
    </div>
  );
}
function RevenueWidget() {
  const [rpm, setRpm] = useState(() => {
    try { return Number(localStorage.getItem("jm_rpm")) || 8; } catch { return 8; }
  });
  const [d, setD] = useState(null);
  useEffect(() => {
    adminRevenueEstimate({ days: 30, rpm }).then(setD).catch(() => setD(null));
    try { localStorage.setItem("jm_rpm", String(rpm)); } catch { /* ignore */ }
  }, [rpm]);

  if (!d) return null;
  const money = (v) => `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="mt-6 border border-border bg-card p-6" data-testid="revenue-widget">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="overline text-primary flex items-center gap-2"><DollarSign className="w-4 h-4" /> Est. ad revenue (last {d.days} days)</p>
          <p className="font-display text-4xl mt-2" data-testid="revenue-total">{money(d.est_revenue)}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {d.pageviews.toLocaleString()} pageviews · {d.unique_sessions.toLocaleString()} sessions · ≈ {money(d.est_daily)}/day · ≈ {money(d.est_monthly)}/month pace
          </p>
        </div>
        <div>
          <label className="overline text-muted-foreground block mb-2">RPM ($ per 1,000 views)</label>
          <input
            type="number" min="1" max="100" step="0.5" value={rpm}
            onChange={(e) => setRpm(Number(e.target.value) || 8)}
            data-testid="revenue-rpm"
            className="w-28 bg-secondary px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary font-mono text-sm"
          />
          <p className="text-[10px] text-muted-foreground mt-1.5 max-w-[220px]">Calibrate from your real AdSense reports. Country weighting applied (US/UK/CA/AU pay most).</p>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <p className="overline text-muted-foreground mb-4">By traffic source</p>
          <div className="space-y-2.5">
            {d.by_source.map((s, i) => (
              <div key={s.name || i} className="flex items-center justify-between text-sm border-b border-border pb-2" data-testid={`rev-source-${String(s.name || "unknown").replace(/[^a-z0-9]/gi, "-")}`}>
                <span className="truncate text-foreground/80">{s.name || "unknown"} <span className="text-muted-foreground text-xs">({s.views.toLocaleString()} views)</span></span>
                <span className="font-mono text-primary">{money(s.est_revenue)}</span>
              </div>
            ))}
            {d.by_source.length === 0 && <p className="text-sm text-muted-foreground">No traffic data yet — estimates appear once visitors arrive.</p>}
          </div>
        </div>
        <div>
          <p className="overline text-muted-foreground mb-4">By visitor country</p>
          <div className="space-y-2.5">
            {d.by_country.map((s, i) => (
              <div key={s.name || i} className="flex items-center justify-between text-sm border-b border-border pb-2" data-testid={`rev-country-${String(s.name || "unknown").replace(/[^a-z0-9]/gi, "-")}`}>
                <span className="truncate text-foreground/80">{s.name || "unknown"} <span className="text-muted-foreground text-xs">({s.views.toLocaleString()} views)</span></span>
                <span className="font-mono text-primary">{money(s.est_revenue)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground mt-5 border-t border-border pt-3">{d.note}</p>
    </div>
  );
}


function DeepAnalytics() {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => { setD(null); adminAnalyticsDetailed(days).then(setD).catch(() => setD(null)); }, [days]);
  const fmtTime = (s) => (s < 60 ? `${s}s` : `${Math.round(s / 60)}m ${Math.round(s % 60)}s`);
  const flag = (cc) => {
    if (!cc || cc.length !== 2 || cc === "??") return "🌍";
    // Regional indicator symbols → flag emoji.
    return String.fromCodePoint(...cc.toUpperCase().split("").map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
  };

  return (
    <div className="mt-12" data-testid="deep-analytics">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h3 className="font-display text-2xl">Deep-dive</h3>
        <div className="flex gap-2">
          {[7, 30, 90].map((n) => (
            <button
              key={n}
              onClick={() => setDays(n)}
              data-testid={`deep-window-${n}`}
              className={`px-3 py-1.5 text-sm font-medium border rounded-full transition-colors ${
                days === n ? "bg-primary text-white border-primary" : "border-border hover:border-primary"
              }`}
            >
              Last {n} days
            </button>
          ))}
        </div>
      </div>
      {!d && <p className="text-muted-foreground">Loading detailed analytics…</p>}
      {d && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard icon={Users} label="Unique sessions" value={d.unique_sessions} sub={`in last ${d.window_days} days`} />
            <StatCard icon={Eye} label="Countries" value={d.top_countries.length} sub="reached" />
            <StatCard icon={TrendingUp} label="Top browser" value={d.browsers[0]?.browser || "–"} sub={`${d.browsers[0]?.count || 0} views`} />
            <StatCard icon={FileText} label="Top device" value={d.devices[0]?.device || "–"} sub={`${d.devices[0]?.count || 0} views`} />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="border border-border p-6 bg-card">
              <p className="overline text-primary mb-6">Top Countries</p>
              <div className="space-y-2">
                {d.top_countries.slice(0, 10).map((c) => (
                  <div key={c.country} className="flex items-center justify-between text-sm border-b border-border pb-2">
                    <span className="flex items-center gap-2"><span className="text-lg">{flag(c.country)}</span> <span className="font-mono">{c.country}</span></span>
                    <span className="font-mono text-primary">{c.count}</span>
                  </div>
                ))}
                {d.top_countries.length === 0 && <p className="text-sm text-muted-foreground">No data yet.</p>}
              </div>
            </div>

            <div className="border border-border p-6 bg-card">
              <p className="overline text-primary mb-6">Top Cities</p>
              <div className="space-y-2">
                {d.top_cities.slice(0, 10).map((c, i) => (
                  <div key={`${c.city}-${i}`} className="flex items-center justify-between text-sm border-b border-border pb-2">
                    <span className="flex items-center gap-2"><span className="text-lg">{flag(c.country)}</span> <span>{c.city || "—"}</span></span>
                    <span className="font-mono text-primary">{c.count}</span>
                  </div>
                ))}
                {d.top_cities.length === 0 && <p className="text-sm text-muted-foreground">City data appears once your site is behind Cloudflare (production is).</p>}
              </div>
            </div>

            <div className="border border-border p-6 bg-card lg:col-span-2">
              <p className="overline text-primary mb-6">Avg. time spent per page</p>
              <div className="space-y-2">
                {d.time_per_page.slice(0, 15).map((t) => (
                  <div key={t.path} className="flex items-center justify-between text-sm border-b border-border pb-2 gap-4">
                    <span className="truncate text-foreground/80 flex-1">{t.path}</span>
                    <span className="font-mono text-muted-foreground text-xs whitespace-nowrap">{t.sample_count} views</span>
                    <span className="font-mono text-primary whitespace-nowrap">{fmtTime(t.avg_seconds)}</span>
                  </div>
                ))}
                {d.time_per_page.length === 0 && <p className="text-sm text-muted-foreground">Waiting for visitors — data appears after a few sessions.</p>}
              </div>
            </div>

            <div className="border border-border p-6 bg-card">
              <p className="overline text-primary mb-6">Devices</p>
              <div className="space-y-2">
                {d.devices.map((row) => (
                  <div key={row.device} className="flex items-center justify-between text-sm border-b border-border pb-2">
                    <span className="capitalize">{row.device}</span>
                    <span className="font-mono text-primary">{row.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-border p-6 bg-card">
              <p className="overline text-primary mb-6">Browsers</p>
              <div className="space-y-2">
                {d.browsers.map((row) => (
                  <div key={row.browser} className="flex items-center justify-between text-sm border-b border-border pb-2">
                    <span>{row.browser}</span>
                    <span className="font-mono text-primary">{row.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-border p-6 bg-card lg:col-span-2">
              <p className="overline text-primary mb-6">Top referrers</p>
              <div className="space-y-2">
                {d.top_referrers.slice(0, 10).map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b border-border pb-2 gap-4">
                    <span className="truncate text-foreground/80 flex-1">{r.referrer}</span>
                    <span className="font-mono text-primary whitespace-nowrap">{r.count}</span>
                  </div>
                ))}
                {d.top_referrers.length === 0 && <p className="text-sm text-muted-foreground">No external referrers in this window.</p>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SocialSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { adminGetSettings().then((d) => setS(d || {})).catch(() => setS({})); }, []);
  if (!s) return <p className="text-muted-foreground">Loading…</p>;
  const social = s.social || {};
  const setSocial = (k, v) => setS((p) => ({ ...p, social: { ...(p.social || {}), [k]: v } }));
  const save = async (e) => {
    e.preventDefault();
    try { await adminUpdateSettings(s); toast.success("Profile updated"); }
    catch { toast.error("Update failed"); }
  };
  return (
    <div data-testid="admin-social">
      <h2 className="font-display text-3xl mb-8">Social & Profile</h2>
      <form onSubmit={save} className="max-w-2xl space-y-5 border border-border p-6 bg-card">
        {[["name", "Name"], ["role", "Role"], ["email", "Public Email"]].map(([k, l]) => (
          <div key={k}><label className="overline text-muted-foreground block mb-2">{l}</label>
            <input value={s[k] || ""} onChange={(e) => setS((p) => ({ ...p, [k]: e.target.value }))} className="w-full bg-secondary px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary" /></div>
        ))}
        <div><label className="overline text-muted-foreground block mb-2">Tagline</label>
          <textarea rows={2} value={s.tagline || ""} onChange={(e) => setS((p) => ({ ...p, tagline: e.target.value }))} className="w-full bg-secondary px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary" /></div>
        <p className="overline text-primary pt-2">Social Media Pages</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            ["twitter", "X / Twitter"],
            ["linkedin", "LinkedIn"],
            ["instagram", "Instagram"],
            ["youtube", "YouTube"],
            ["facebook", "Facebook"],
            ["tiktok", "TikTok"],
            ["threads", "Threads"],
            ["pinterest", "Pinterest"],
            ["whatsapp", "WhatsApp (business chat link)"],
            ["telegram", "Telegram"],
            ["bluesky", "Bluesky"],
            ["github", "GitHub"],
          ].map(([k, label]) => (
            <div key={k}><label className="overline text-muted-foreground block mb-2">{label}</label>
              <input value={social[k] || ""} onChange={(e) => setSocial(k, e.target.value)} placeholder={`https://…`} data-testid={`social-${k}-input`} className="w-full bg-secondary px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary" /></div>
          ))}
        </div>

        <p className="overline text-primary pt-4">Monetization — Google AdSense</p>
        <p className="text-xs text-muted-foreground -mt-3">Optional. Paste your AdSense publisher ID (starts with <code>ca-pub-</code>). Ads will start rendering across the site once approved by Google. See <b>Help &amp; Instructions</b> for AdSense signup.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="overline text-muted-foreground block mb-2">AdSense Publisher ID</label>
            <input value={s.adsense_pub_id || ""} onChange={(e) => setS((p) => ({ ...p, adsense_pub_id: e.target.value }))} placeholder="ca-pub-1234567890123456" data-testid="adsense-pub-input" className="w-full bg-secondary px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary font-mono text-xs" />
          </div>
          <div>
            <label className="overline text-muted-foreground block mb-2">Default Blog Ad Slot ID</label>
            <input value={s.adsense_blog_slot || ""} onChange={(e) => setS((p) => ({ ...p, adsense_blog_slot: e.target.value }))} placeholder="1234567890" data-testid="adsense-slot-input" className="w-full bg-secondary px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary font-mono text-xs" />
          </div>
        </div>

        <p className="overline text-primary pt-4">Monetization — Tip Jar</p>
        <p className="text-xs text-muted-foreground -mt-3">Optional. Paste a Buy Me a Coffee, Ko-fi, PayPal.me or Stripe payment link. Shows up on every blog post so readers can support your work.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="overline text-muted-foreground block mb-2">Tip Jar URL</label>
            <input value={social.tipjar || ""} onChange={(e) => setSocial("tipjar", e.target.value)} placeholder="https://buymeacoffee.com/juliusmason" data-testid="social-tipjar-input" className="w-full bg-secondary px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <div>
            <label className="overline text-muted-foreground block mb-2">Tip Jar Button Label</label>
            <input value={s.tipjar_label || ""} onChange={(e) => setS((p) => ({ ...p, tipjar_label: e.target.value }))} placeholder="Buy me a coffee" data-testid="social-tipjar-label" className="w-full bg-secondary px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary" />
          </div>
        </div>

        <p className="overline text-primary pt-4">Analytics & Tracking IDs</p>
        <p className="text-xs text-muted-foreground -mt-3">Optional. Paste IDs from your analytics accounts — scripts are injected site-wide automatically once saved. Nothing is hard-coded.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            ["ga4_id", "Google Analytics 4 ID", "G-XXXXXXXXXX"],
            ["gtm_id", "Google Tag Manager ID", "GTM-XXXXXXX"],
            ["meta_pixel_id", "Meta Pixel ID", "1234567890"],
            ["linkedin_partner_id", "LinkedIn Partner ID", "1234567"],
          ].map(([k, label, ph]) => (
            <div key={k}>
              <label className="overline text-muted-foreground block mb-2">{label}</label>
              <input
                value={(s.analytics || {})[k] || ""}
                onChange={(e) => setS((p) => ({ ...p, analytics: { ...(p.analytics || {}), [k]: e.target.value.trim() } }))}
                placeholder={ph}
                data-testid={`analytics-${k}`}
                className="w-full bg-secondary px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary font-mono text-xs"
              />
            </div>
          ))}
        </div>

        <button type="submit" data-testid="save-social" className="bg-primary text-white px-6 py-3 font-semibold hover:bg-primary/90 active:scale-95 transition">Save Profile</button>
      </form>
    </div>
  );
}

function Messages() {
  const [items, setItems] = useState(null);
  useEffect(() => { adminContacts().then(setItems).catch(() => setItems([])); }, []);
  if (!items) return <p className="text-muted-foreground">Loading…</p>;
  return (
    <div data-testid="admin-messages">
      <h2 className="font-display text-3xl mb-8">Messages ({items.length})</h2>
      <div className="space-y-4">
        {items.length === 0 && <p className="text-muted-foreground">No messages yet.</p>}
        {items.map((m) => (
          <div key={m.id} className="border border-border p-6 bg-card">
            <div className="flex items-center justify-between mb-2">
              <p className="font-display text-xl">{m.name} {m.company && <span className="text-muted-foreground text-base">· {m.company}</span>}</p>
              <span className="font-mono text-xs text-muted-foreground">{(m.created_at || "").slice(0, 10)}</span>
            </div>
            <a href={`mailto:${m.email}`} className="text-primary text-sm link-underline">{m.email}</a>
            {m.subject && <p className="mt-3 font-medium">{m.subject}</p>}
            <p className="mt-2 text-muted-foreground leading-relaxed">{m.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Subscribers() {
  const [items, setItems] = useState(null);
  useEffect(() => { adminSubscribers().then(setItems).catch(() => setItems([])); }, []);
  if (!items) return <p className="text-muted-foreground">Loading…</p>;
  return (
    <div data-testid="admin-subscribers">
      <h2 className="font-display text-3xl mb-8">Subscribers ({items.length})</h2>
      <div className="border border-border divide-y divide-border">
        {items.length === 0 && <p className="p-6 text-muted-foreground">No subscribers yet.</p>}
        {items.map((s) => (
          <div key={s.id} className="flex items-center justify-between p-4">
            <span>{s.email}</span>
            <span className="font-mono text-xs text-muted-foreground">{s.source} · {(s.created_at || "").slice(0, 10)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => { if (user === false) navigate("/admin/login"); }, [user, navigate]);

  if (user === null) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-border border-t-primary rounded-full animate-spin" /></div>;
  if (!user) return null;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen flex flex-col lg:flex-row">
      <aside className="lg:w-64 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-border bg-card flex flex-col">
        <div className="p-6 border-b border-border">
          <p className="font-display text-xl">JULIUS<span className="text-primary">.</span>MASON</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Admin Panel</p>
        </div>
        <nav className="flex-1 p-3 flex lg:flex-col gap-1 overflow-x-auto">
          {NAV.map(([key, label, Icon]) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)} className={`flex items-center gap-3 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${tab === key ? "bg-primary text-white" : "text-foreground/80 hover:bg-secondary"}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-border space-y-1">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-3 text-sm text-foreground/80 hover:bg-secondary transition-colors"><ExternalLink className="w-4 h-4" /> View Site</a>
          <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-foreground/80 hover:bg-secondary transition-colors"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      </aside>

      <main className="flex-1 p-6 md:p-10 max-w-full overflow-x-hidden">
        {tab === "overview" && <Overview />}
        {tab === "leads" && <AdminLeads />}
        {tab === "pricing" && <AdminPricing />}
        {tab === "backlinks" && <AdminBacklinks />}
        {tab === "previews" && <AdminPreviews />}
        {tab === "help" && <AdminHelp />}
        {tab === "blog" && <CrudManager collection="blog" title="Blog Posts" fields={BLOG_FIELDS} defaults={{ featured: false, author: "Julius Mason" }} aiGenerate={(topic, aff) => aiGenerateBlog(topic, undefined, undefined, aff)} />}
        {tab === "auto-blog" && <AutoBlog />}
        {tab === "ads" && <CrudManager collection="ads" title="Ads" fields={AD_FIELDS} defaults={{ placement: "sidebar", active: true }} intro={<>Use these slots either for sold sponsorships <b>or</b> for your own high-priority affiliate offers. Every impression &amp; click is tracked in Overview.</>} />}
        {tab === "floor-planner" && <CrudManager collection="floor_packs" title="Floor Planner — Makasa Packs" fields={[
          { key: "name", label: "Pack name", type: "text" },
          { key: "style", label: "Style", type: "text" },
          { key: "badge", label: "Badge", type: "text" },
          { key: "price_eur", label: "Price EUR", type: "number" },
          { key: "url", label: "Order URL", type: "text", full: true },
          { key: "description", label: "Description", type: "textarea", full: true },
          { key: "items", label: "Placeable items (JSON: id, name, category, width_m, depth_m, height_m, color, qty, round?)", type: "json", full: true },
          { key: "included", label: "Also included (comma-separated)", type: "list", full: true },
        ]} intro={<>The furniture packs users place in the <b>/tools/floor-planner</b> tool. Edit dimensions (meters), colors and prices here — changes go live immediately. The tool is at /tools/floor-planner.</>} />}
        {tab === "floor-planner" && <div className="mt-10"><AdminFloorModels /></div>}
        {tab === "floor-planner" && <div className="mt-10"><CrudManager collection="floor_leads" title="Floor Planner — Leads (PDF downloads)" fields={[
          { key: "email", label: "Email", type: "text" },
          { key: "name", label: "Name", type: "text" },
          { key: "brand", label: "Brand", type: "text" },
          { key: "project_title", label: "Project", type: "text" },
          { key: "packs", label: "Packs (comma-separated)", type: "list", full: true },
        ]} intro={<>Everyone who downloads a floor-planner PDF leaves their email here — the Makasa sales leads.</>} /></div>}
        {tab === "programs" && <CrudManager collection="clients" title="Programs & Companies" fields={PROGRAMS_FIELDS} intro={<>Programs, products and companies you created or support — shown on the public <b>Programs</b> page (/clients). Add AVENO here first.</>} />}
        {tab === "affiliate" && (
          <div className="space-y-10">
            <AmazonBulk />
            <CrudManager collection="products" title="Affiliate Links" fields={PRODUCT_FIELDS} defaults={{ type: "affiliate", price: 0, category: "Website Resources" }} filter={(p) => p.type === "affiliate"} intro={
              <>
                <p className="font-semibold text-foreground mb-2">This is your passive income engine — and your entire Shop. Every affiliate link you add here appears on the Shop page (/shop), blog & writing sidebars, and tool pages. When your Shopify store is ready, its products will slot in alongside these.</p>
                <p>Best programs to join: <b>Amazon Associates, Impact.com, PartnerStack, ShareASale, CJ.com</b>. See <b>Help &amp; Instructions</b> for direct signup links.</p>
              </>
            } />
          </div>
        )}
        {tab === "social" && <><SocialSettings /><AdminAutoPost /></>}
        {tab === "messages" && <Messages />}
        {tab === "subscribers" && <Subscribers />}
      </main>
    </div>
  );
}
